import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const {
    lang,
    setLang,
    t
  } = useLanguage();
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);
  const navLinks = [{
    name: t('nav.home'),
    path: '/'
  }, {
    name: t('nav.services'),
    path: '/services'
  }, {
    name: t('nav.about'),
    path: '/about'
  }, {
    name: t('nav.contact'),
    path: '/contact'
  }];
  return <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white/95 backdrop-blur-md shadow-pink-soft py-3' : 'bg-white py-5'}`} data-id="element-88">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-89">
        <div className="flex justify-between items-center" data-id="element-90">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group" data-id="element-91">
            <img src="https://cdn.magicpatterns.com/uploads/s45KS2J3qfedmHm2iX4JF5/ChatGPT_Image_May_5,_2026,_05_53_25_PM.png" alt="Lucy Cleaning Services" className="h-10 sm:h-12 object-contain transition-transform group-hover:scale-105" data-id="element-92" />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8" data-id="element-93">
            {navLinks.map(link => <Link key={link.path} to={link.path} className={`text-sm font-medium transition-colors hover:text-pink-primary relative group ${location.pathname === link.path ? 'text-pink-primary' : 'text-navy'}`} data-id="element-94">
                {link.name}
                <span className={`absolute -bottom-1 left-0 w-full h-0.5 bg-pink-primary transform origin-left transition-transform duration-300 ${location.pathname === link.path ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'}`} data-id="element-95" />
              </Link>)}
          </nav>

          {/* Actions */}
          <div className="hidden md:flex items-center gap-6" data-id="element-96">
            {/* Language Switcher */}
            <div className="flex items-center bg-pink-lighter rounded-full p-1 border border-pink-100" data-id="element-97">
              <button onClick={() => setLang('en')} className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${lang === 'en' ? 'bg-white shadow-sm text-pink-primary' : 'text-gray-500 hover:text-navy'}`} data-id="element-98">
                🇺🇸 EN
              </button>
              <button onClick={() => setLang('pt')} className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${lang === 'pt' ? 'bg-white shadow-sm text-pink-primary' : 'text-gray-500 hover:text-navy'}`} data-id="element-99">
                🇧🇷 PT
              </button>
            </div>

            <Link to="/booking" className="bg-pink-primary text-white px-6 py-2.5 rounded-full font-medium hover:bg-pink-hover transition-colors shadow-pink-soft flex items-center gap-2" data-id="element-100">
              <Sparkles className="w-4 h-4" data-id="element-101" />
              {t('nav.bookNow')}
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button className="md:hidden p-2 text-navy hover:text-pink-primary transition-colors" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} data-id="element-102">
            {isMobileMenuOpen ? <X className="w-6 h-6" data-id="element-103" /> : <Menu className="w-6 h-6" data-id="element-104" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence data-id="element-105">
        {isMobileMenuOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="md:hidden bg-white border-t border-pink-50 overflow-hidden" data-id="element-106">
            <div className="px-4 py-6 flex flex-col gap-4" data-id="element-107">
              {navLinks.map(link => <Link key={link.path} to={link.path} className={`text-lg font-medium p-2 rounded-lg ${location.pathname === link.path ? 'bg-pink-lighter text-pink-primary' : 'text-navy'}`} data-id="element-108">
                  {link.name}
                </Link>)}

              <div className="h-px bg-pink-50 my-2" data-id="element-109" />

              <div className="flex items-center gap-4 p-2" data-id="element-110">
                <span className="text-sm text-gray-500" data-id="element-111">Language:</span>
                <button onClick={() => setLang('en')} className={`px-3 py-1 rounded-full text-sm font-medium border ${lang === 'en' ? 'bg-pink-primary text-white border-pink-primary' : 'bg-white text-navy border-gray-200'}`} data-id="element-112">
                  🇺🇸 EN
                </button>
                <button onClick={() => setLang('pt')} className={`px-3 py-1 rounded-full text-sm font-medium border ${lang === 'pt' ? 'bg-pink-primary text-white border-pink-primary' : 'bg-white text-navy border-gray-200'}`} data-id="element-113">
                  🇧🇷 PT
                </button>
              </div>

              <Link to="/booking" className="mt-4 bg-pink-primary text-white px-6 py-3 rounded-xl font-medium text-center hover:bg-pink-hover transition-colors shadow-pink-soft flex items-center justify-center gap-2" data-id="element-114">
                <Sparkles className="w-5 h-5" data-id="element-115" />
                {t('nav.bookNow')}
              </Link>
            </div>
          </motion.div>}
      </AnimatePresence>
    </header>;
};